import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { useAIMateExperienceTracker } from '@atlassian/conversation-assistant-instrumentation/main';
import { useAssistanceService } from '@atlassian/conversation-assistant-service/services/assistance-service/main';
import { EMPTY_CONVERSATION_ID } from '@atlassian/conversation-assistant-store/controllers/chat';
import {
	CHAT_STORE_STATUS,
	isStoreAssistant,
	isStoreHuman,
	type StoreChatItem,
} from '@atlassian/conversation-assistant-store/controllers/chat/types';
import { useChatMessages } from '@atlassian/conversation-assistant-store/controllers/chat/use-chat-messages';
import { type ShareConversationDisabledReason } from '@atlassian/conversation-assistant-ui-components/ui/ChatHeaderMoreMenu/messages';
import { type ShareConversationSubmitPayload } from '@atlassian/conversation-assistant-ui-components/ui/ShareConversationModal';
import {
	buildShareConversationPreviewUrl,
	buildShareConversationUrl,
	getOrgIdFromLocation,
} from '@atlassian/conversation-assistant-ui-components/ui/ShareConversationModal/buildShareConversationUrl';
type ShareType = 'organization' | 'specific-users';

type ShareConversationUser = {
	id: string;
	name: string;
	avatarUrl?: string;
};

type ShareDetailsResponse = {
	shareToken: string;
	shareType: 'ORG' | 'SPECIFIC_USERS';
	allowedUserAris?: string[] | null;
	creatorAri?: string | null;
};

const USER_ARI_PREFIX = 'ari:cloud:identity::user/';

const toUserAri = (accountId: string) => `${USER_ARI_PREFIX}${accountId}`;

const getAccountIdFromUserAri = (userAri: string): string =>
	userAri.startsWith(USER_ARI_PREFIX) ? userAri.slice(USER_ARI_PREFIX.length) : userAri;

const isCurrentUserAri = (userAri: string, currentUserId?: string): boolean =>
	Boolean(currentUserId && getAccountIdFromUserAri(userAri) === currentUserId);

const withoutCurrentUserAri = (userAris: string[], currentUserId?: string): string[] =>
	userAris.filter((userAri) => !isCurrentUserAri(userAri, currentUserId));

const toShareConversationUser = (userAri: string): ShareConversationUser => {
	const accountId = getAccountIdFromUserAri(userAri);
	return {
		id: accountId,
		name: accountId,
	};
};

const toInitialShareType = (shareType: 'ORG' | 'SPECIFIC_USERS'): ShareType =>
	shareType === 'ORG' ? 'organization' : 'specific-users';

const toShareAccessPayload = (
	response: ShareDetailsResponse,
	currentUserId?: string,
): ShareConversationSubmitPayload => {
	const shareType = toInitialShareType(response.shareType);
	return {
		shareType,
		addUserAris:
			shareType === 'specific-users'
				? withoutCurrentUserAri(response.allowedUserAris ?? [], currentUserId)
				: [],
	};
};

const accessPayloadsEqual = (
	left: ShareConversationSubmitPayload | undefined,
	right: ShareConversationSubmitPayload | undefined,
): boolean => {
	if (!left || !right) {
		return left === right;
	}
	if (left.shareType !== right.shareType || left.addUserAris.length !== right.addUserAris.length) {
		return false;
	}
	return left.addUserAris.every((userAri, index) => userAri === right.addUserAris[index]);
};

const isNotFoundError = (err: unknown): boolean =>
	(err as { errorAttributes?: { statusCode?: number } } | undefined)?.errorAttributes
		?.statusCode === 404;

/**
 * State + behaviour for the share-snapshot modal.
 *
 * The hook owns the integration end-to-end so that callers only need to
 * supply the conversation context:
 * - Locates the latest persisted message id in the current conversation and
 *   uses it as the snapshot boundary sent to the backend. Empty assistant
 *   responses are skipped so they cannot be used as unshareable boundaries.
 * - Opens by calling the non-mutating share
 *   metadata GET endpoint to hydrate existing access without creating a share.
 * - Saves access with `AssistanceService.replaceConversationShare`.
 * - Maps the UI's `'organization' | 'specific-users'` selection to the
 *   backend's `'ORG' | 'SPECIFIC_USERS'` enum and conditionally includes
 *   `allowedUserAris` (the backend rejects payloads that include it for
 *   organisation-wide shares).
 *
 * On a successful save the hook builds the shareable URL from the returned
 * `shareToken` and exposes it as `shareUrl`. The modal stays open and
 * switches to its "link view" (State 2) where the user can copy the link.
 * Opening or closing the modal clears `shareUrl`, so Copy link will create
 * the share before copying.
 */
export type ShareConversationModalState = {
	/** Whether the modal is currently mounted. */
	isOpen: boolean;
	/** Whether a submit is in flight. Drives the modal's loading overlay. */
	isSubmitting: boolean;
	/** Whether existing share metadata is being loaded for the initial modal state. */
	isLoadingInitialState: boolean;
	/**
	 * Raw inline error to show below the share controls, if any. Set for every
	 * failure, including known share limits: the modal prefers its own copy
	 * for those via `errorCode`, but falls back to this text when it has none.
	 */
	errorMessage: string | undefined;
	/** Backend error code from the last failed request, if available. */
	errorCode: string | undefined;
	/**
	 * Whether the Copy link action should be enabled. False when there is no
	 * active conversation or the conversation has no messages yet to
	 * anchor the snapshot.
	 */
	canGenerate: boolean;
	/**
	 * Reason the share entry should explain when `canGenerate` is false.
	 * Undefined when the chat is shareable or the share entry is not shown.
	 */
	shareDisabledReason: ShareConversationDisabledReason | undefined;
	/**
	 * The generated shareable link, once a PATCH has succeeded. When set,
	 * the modal renders its "link view" (State 2). `undefined` while the
	 * modal is in its default "generate" state.
	 */
	shareUrl: string | undefined;
	/**
	 * Sender preview link used by the "See what others can see" affordance
	 * and the link-view Preview button. Uses Home's static preview route with
	 * conversation/message identifiers.
	 */
	seeWhatOthersCanSeeHref: string | undefined;
	/** Initial visibility state hydrated from the share management API. */
	initialShareType: ShareType | undefined;
	/** Initial people picker state hydrated from the share management API. */
	initialSelectedUsers: ShareConversationUser[] | undefined;
	/** User details for the non-removable owner row in the specific access list. */
	initialOwnerUser: ShareConversationUser | undefined;
	/** Whether access changes should save immediately instead of waiting for the primary CTA. */
	autoSaveShareAccess: boolean;
	/** Whether an immediate access save is in flight. */
	isSavingShareAccess: boolean;
	/** Whether the modal may submit SPECIFIC_USERS with an empty recipient list. */
	allowEmptySpecificUsers: boolean;
	/** Whether the modal should render selected people in the access-list treatment. */
	showSpecificPeopleAccessList: boolean;
	/**
	 * Opens the modal and clears any stale error and previous link.
	 *
	 * When called with a `messageId`, that message becomes the snapshot
	 * boundary — the generated share includes every message up to and
	 * including it, so the user can "share chat up to this point".
	 * When called without an argument the boundary falls back to the latest
	 * shareable message (the whole-conversation share used by the chat
	 * header).
	 */
	open: (messageId?: string) => void;
	/** Closes the modal and clears any stale error and previous link. */
	close: () => void;
	/** Saves changed access immediately in the share-details API flow. */
	handleShareAccessChange: (payload: ShareConversationSubmitPayload) => Promise<boolean>;
	/** Waits for any optimistic access changes to be server-confirmed. */
	flushShareAccessChanges: () => Promise<void>;
	/** Completes the share experience after the link reaches the clipboard. */
	handleCopyLinkSuccess: () => void;
	/** Fails the share experience when copying the link does not complete. */
	handleCopyLinkFailure: (error: unknown) => void;
	/**
	 * Whether a specific message can be used as a "share chat up to this point" boundary,
	 * using the same shareability rule as the latest-message boundary. Per-
	 * message share buttons call this to disable themselves on messages that
	 * cannot be shared (e.g. an empty/cancelled assistant turn), so they never
	 * offer a share that would silently fall back to a different boundary.
	 */
	isMessageShareable: (messageId: string) => boolean;
	/**
	 * Copy-link submit handler for the share-details API flow. It creates the
	 * initial share and stores the link without toggling the full-modal submit
	 * overlay; the footer button owns its own copying/loading state.
	 */
	handleCopyLink: (payload: ShareConversationSubmitPayload) => Promise<string | undefined>;
};

export type UseShareConversationModalStateArgs = {
	/**
	 * Id of the conversation being shared. When omitted, equal to
	 * `EMPTY_CONVERSATION_ID`, or when the conversation has no messages
	 * yet, or when there is no persisted message id, the primary
	 * button is disabled (`canGenerate === false`) to prevent submits that
	 * the backend would reject or snapshot a conversation while Rovo is
	 * still responding.
	 */
	conversationId?: string;
	/**
	 * Organization id of the current tenant. When supplied, generated share
	 * links are scoped through Home's `/o/{orgId}` route so Home can resolve
	 * the shared chat from the sender's organization context.
	 */
	orgId?: string;
	/**
	 * Site id appended to generated share and preview links so Home can
	 * resolve the shared chat in the originating site context.
	 */
	siteId?: string;
	/**
	 * Status of the active conversation. Used to block sharing while the
	 * backend is waiting for or streaming an assistant response.
	 */
	conversationStatus?: CHAT_STORE_STATUS;
	/**
	 * AAID of the current user. Used to omit the owner from specific-user
	 * replacement payloads and access-list rows.
	 */
	currentUserId?: string;
	/**
	 * Optional override for the underlying submit. When supplied, the hook
	 * does not call `AssistanceService.replaceConversationShare` itself.
	 * Intended for tests and non-default embeddings.
	 *
	 * May resolve the shareable URL to drive the modal's "link view"
	 * (State 2). Returning `undefined`/`void` keeps the previous behaviour
	 * of closing the modal on success.
	 */
	onSubmit?: (payload: ShareConversationSubmitPayload) => Promise<string | void> | string | void;
	/**
	 * Optional override for the host (origin) used to build the user-facing
	 * share link. When omitted, `buildShareConversationUrl` defaults to the
	 * appropriate Home host for the current origin. Supply this primarily so
	 * staging/dev testers can target a specific non-production Home host.
	 */
	shareBaseUrl?: string;
};

const isUuidMessageId = (id: unknown): id is string =>
	typeof id === 'string' &&
	/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);

const hasAssistantResponseContent = (content: unknown): boolean =>
	typeof content === 'string' ? content.trim().length > 0 : content != null;

/**
 * Returns the snapshot boundary id for a single message, or `undefined` when
 * the message cannot be used as a share boundary.
 *
 * This is the single source of truth for "can this message be shared": both
 * the latest-message boundary and per-message ("share chat up to this point") shares run
 * through it, so a per-message share is only ever offered/sent for a message
 * that would also be a valid latest boundary. Assistant messages need both a
 * backend UUID and response content because cancelled turns can leave an empty
 * FINAL_RESPONSE with a UUID that the share API does not accept.
 */
const getShareableBoundaryId = (message: StoreChatItem): string | undefined => {
	if (isStoreAssistant(message)) {
		const assistantMessage = message.content?.message?.message;
		const id = assistantMessage?.id;
		if (isUuidMessageId(id) && hasAssistantResponseContent(assistantMessage?.content)) {
			return id;
		}
		return undefined;
	}
	if (isStoreHuman(message)) {
		const id = message.content.id;
		if (isUuidMessageId(id)) {
			return id;
		}
	}
	return undefined;
};

export const useShareConversationModalState = ({
	conversationId,
	orgId,
	siteId,
	conversationStatus,
	currentUserId,
	onSubmit,
	shareBaseUrl,
}: UseShareConversationModalStateArgs = {}): ShareConversationModalState => {
	const [isOpen, setIsOpen] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isLoadingInitialState, setIsLoadingInitialState] = useState(false);
	const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
	const [errorCode, setErrorCode] = useState<string | undefined>(undefined);
	const [shareUrl, setShareUrl] = useState<string | undefined>(undefined);
	const [seeWhatOthersCanSeeHref, setSeeWhatOthersCanSeeHref] = useState<string | undefined>(
		undefined,
	);
	const [initialShareType, setInitialShareType] = useState<ShareType | undefined>(undefined);
	const [initialSelectedUsers, setInitialSelectedUsers] = useState<
		ShareConversationUser[] | undefined
	>(undefined);
	const [initialOwnerUser, setInitialOwnerUser] = useState<ShareConversationUser | undefined>(
		undefined,
	);
	const [isSavingShareAccess, setIsSavingShareAccess] = useState(false);
	const shareDetailsRequestIdRef = useRef(0);
	const confirmedShareAccessRef = useRef<ShareConversationSubmitPayload | undefined>(undefined);
	const desiredShareAccessRef = useRef<ShareConversationSubmitPayload | undefined>(undefined);
	const pendingShareAccessRef = useRef<ShareConversationSubmitPayload | undefined>(undefined);
	const shareAccessSavePromiseRef = useRef<Promise<void> | undefined>(undefined);
	// When the modal is opened from a specific message's share button, that
	// message id is held here and used as the snapshot boundary instead of
	// the conversation's latest shareable message. Cleared on open()/close()
	// so the header share reverts to the whole-conversation boundary.
	const [overrideMessageId, setOverrideMessageId] = useState<string | undefined>(undefined);

	/** Records a failed share request for the modal to present. */
	const applyShareError = useCallback((err: unknown) => {
		const errorCode = (err as { errorAttributes?: { errorCode?: unknown } } | null | undefined)
			?.errorAttributes?.errorCode;
		setErrorCode(typeof errorCode === 'string' ? errorCode : undefined);
		setErrorMessage(err instanceof Error ? err.message : String(err));
	}, []);

	const clearShareError = useCallback(() => {
		setErrorCode(undefined);
		setErrorMessage(undefined);
	}, []);

	const assistanceService = useAssistanceService();
	const experienceTracker = useAIMateExperienceTracker();

	const failShareSession = useCallback(
		(err: unknown) => {
			experienceTracker.fail({
				name: ExperienceName.SHARE_CONVERSATION_SESSION,
				error: err instanceof Error ? err : new Error(String(err)),
			});
		},
		[experienceTracker],
	);

	const succeedShareSession = useCallback(() => {
		experienceTracker.succeed({ name: ExperienceName.SHARE_CONVERSATION_SESSION });
	}, [experienceTracker]);

	// Every unexpected error inside the modal surfaces to the user and fails
	// the funnel experience, so the two always happen together. The clipboard
	// callbacks are the exception: they report directly to `failShareSession`
	// because the modal renders its own copy-failure feedback.
	const reportShareError = useCallback(
		(err: unknown) => {
			applyShareError(err);
			failShareSession(err);
		},
		[applyShareError, failShareSession],
	);

	const abortShareSession = useCallback(
		(reason: string) => {
			experienceTracker.abort({
				name: ExperienceName.SHARE_CONVERSATION_SESSION,
				reason,
			});
		},
		[experienceTracker],
	);

	useEffect(
		() => () => {
			abortShareSession('Share conversation modal unmounted before the link was copied');
		},
		[abortShareSession],
	);

	// `useChatMessages` requires a string; pass `EMPTY_CONVERSATION_ID` when
	// the caller has no active conversation so the selector still returns
	// an (empty) array and we don't violate Rules of Hooks.
	const [messages] = useChatMessages({
		conversationId: conversationId ?? EMPTY_CONVERSATION_ID,
	});

	const latestMessageId = useMemo(() => {
		// Walk backward to find the newest shareable snapshot boundary, using
		// the same per-message shareability rule applied to per-message shares.
		for (let i = messages.length - 1; i >= 0; i -= 1) {
			const id = getShareableBoundaryId(messages[i]);
			if (id) {
				return id;
			}
		}
		return undefined;
	}, [messages]);

	// Set of message ids in the current conversation that are valid snapshot
	// boundaries, derived from the same shareability rule as `latestMessageId`.
	// Used to validate a caller-supplied `overrideMessageId` so a message that
	// is not shareable (or no longer present) cannot be sent to the share API —
	// in that case we fall back to the latest boundary.
	const validBoundaryMessageIds = useMemo(() => {
		const ids = new Set<string>();
		for (const message of messages) {
			const id = getShareableBoundaryId(message);
			if (id) {
				ids.add(id);
			}
		}
		return ids;
	}, [messages]);

	// The snapshot boundary actually sent to the share API. A per-message
	// override wins only when it passes the same shareability check as
	// `latestMessageId`; otherwise we anchor to the conversation's latest
	// shareable message (the whole-conversation share used by the chat header).
	const boundaryMessageId =
		overrideMessageId && validBoundaryMessageIds.has(overrideMessageId)
			? overrideMessageId
			: latestMessageId;

	const hasActiveConversation = Boolean(conversationId && conversationId !== EMPTY_CONVERSATION_ID);
	const isRovoResponseInProgress =
		conversationStatus === CHAT_STORE_STATUS.awaitingResponse ||
		conversationStatus === CHAT_STORE_STATUS.typingResponse;
	const resolvedOrgId = orgId || getOrgIdFromLocation();
	const canGenerate =
		hasActiveConversation && Boolean(latestMessageId) && !isRovoResponseInProgress;
	const shareDisabledReason: ShareConversationDisabledReason | undefined = isRovoResponseInProgress
		? 'rovo-response-in-progress'
		: hasActiveConversation && !latestMessageId
			? 'missing-valid-response'
			: undefined;

	const applyConfirmedShareAccess = useCallback(
		(
			payload: ShareConversationSubmitPayload | undefined,
			options: { resetDesiredAccess?: boolean; updateInitialState?: boolean } = {},
		) => {
			const { resetDesiredAccess = true, updateInitialState = true } = options;
			confirmedShareAccessRef.current = payload;
			if (resetDesiredAccess) {
				desiredShareAccessRef.current = payload;
				pendingShareAccessRef.current = undefined;
			}
			if (!updateInitialState) {
				return;
			}

			if (!payload) {
				setInitialShareType(undefined);
				setInitialSelectedUsers(undefined);
				setInitialOwnerUser(undefined);
				return;
			}

			setInitialShareType(payload.shareType);
			setInitialSelectedUsers(
				payload.shareType === 'specific-users'
					? payload.addUserAris.map(toShareConversationUser)
					: [],
			);
		},
		[],
	);

	const applyShareDetailsResponse = useCallback(
		(
			response: ShareDetailsResponse,
			options: { resetDesiredAccess?: boolean; updateInitialState?: boolean } = {},
		): string => {
			const { resetDesiredAccess = true, updateInitialState = true } = options;
			const confirmedPayload = toShareAccessPayload(response, currentUserId);
			const shareType = confirmedPayload.shareType;
			const allowedUserAris = withoutCurrentUserAri(response.allowedUserAris ?? [], currentUserId);
			const ownerAri =
				response.creatorAri ?? (currentUserId ? toUserAri(currentUserId) : undefined);
			confirmedShareAccessRef.current = confirmedPayload;
			if (resetDesiredAccess) {
				desiredShareAccessRef.current = confirmedPayload;
				pendingShareAccessRef.current = undefined;
			}
			if (!updateInitialState) {
				return buildShareConversationUrl(response.shareToken, shareBaseUrl, resolvedOrgId, siteId);
			}
			const nextInitialSelectedUsers = allowedUserAris.map(toShareConversationUser);
			const nextInitialOwnerUser = ownerAri ? toShareConversationUser(ownerAri) : undefined;
			setInitialShareType(shareType);
			setInitialSelectedUsers(nextInitialSelectedUsers);
			setInitialOwnerUser(nextInitialOwnerUser);
			return buildShareConversationUrl(response.shareToken, shareBaseUrl, resolvedOrgId, siteId);
		},
		[currentUserId, resolvedOrgId, shareBaseUrl, siteId],
	);

	const buildPreviewHref = useCallback(
		(boundaryOverride?: string) => {
			const previewBoundaryMessageId = boundaryOverride ?? boundaryMessageId;
			if (
				!hasActiveConversation ||
				!conversationId ||
				!previewBoundaryMessageId ||
				isRovoResponseInProgress
			) {
				return undefined;
			}

			return buildShareConversationPreviewUrl({
				conversationId,
				messageId: previewBoundaryMessageId,
				baseUrl: shareBaseUrl,
				orgId: resolvedOrgId,
				siteId,
			});
		},
		[
			boundaryMessageId,
			conversationId,
			hasActiveConversation,
			isRovoResponseInProgress,
			resolvedOrgId,
			shareBaseUrl,
			siteId,
		],
	);

	const hydrateShareDetails = useCallback(
		async (boundaryOverride?: string) => {
			// Callers can pass the boundary explicitly to avoid a
			// not-yet-committed `overrideMessageId` state update.
			const hydrateBoundaryMessageId = boundaryOverride ?? boundaryMessageId;
			if (
				!hasActiveConversation ||
				!conversationId ||
				!hydrateBoundaryMessageId ||
				isRovoResponseInProgress
			) {
				return;
			}

			const requestId = shareDetailsRequestIdRef.current + 1;
			shareDetailsRequestIdRef.current = requestId;
			setIsLoadingInitialState(true);

			try {
				const response = await assistanceService.getConversationShareMetadata({
					conversationId,
					messageId: hydrateBoundaryMessageId,
				});
				if (shareDetailsRequestIdRef.current === requestId) {
					const linkUrl = applyShareDetailsResponse(response);
					setShareUrl(linkUrl);
					setSeeWhatOthersCanSeeHref(buildPreviewHref(hydrateBoundaryMessageId));
				}
			} catch (err) {
				if (shareDetailsRequestIdRef.current === requestId) {
					if (isNotFoundError(err)) {
						// Expected: the conversation has not been shared yet.
						applyConfirmedShareAccess(undefined);
						setShareUrl(undefined);
						setSeeWhatOthersCanSeeHref(buildPreviewHref(hydrateBoundaryMessageId));
					} else {
						setSeeWhatOthersCanSeeHref(undefined);
						reportShareError(err);
					}
				}
			} finally {
				if (shareDetailsRequestIdRef.current === requestId) {
					setIsLoadingInitialState(false);
				}
			}
		},
		[
			assistanceService,
			applyConfirmedShareAccess,
			applyShareDetailsResponse,
			buildPreviewHref,
			conversationId,
			hasActiveConversation,
			isRovoResponseInProgress,
			boundaryMessageId,
			reportShareError,
		],
	);

	const open = useCallback(
		(messageId?: string) => {
			experienceTracker.start({
				name: ExperienceName.SHARE_CONVERSATION_SESSION,
				id: conversationId ?? 'unknown',
				attributes: {
					conversationId,
					isPerMessageShare: Boolean(messageId),
				},
			});
			clearShareError();
			setShareUrl(undefined);
			setSeeWhatOthersCanSeeHref(undefined);
			setInitialShareType(undefined);
			setInitialSelectedUsers(undefined);
			setInitialOwnerUser(undefined);
			setIsSavingShareAccess(false);
			setIsLoadingInitialState(false);
			confirmedShareAccessRef.current = undefined;
			desiredShareAccessRef.current = undefined;
			pendingShareAccessRef.current = undefined;
			shareAccessSavePromiseRef.current = undefined;
			// A per-message share anchors the snapshot to `messageId`; opening
			// without one (e.g. from the chat header) shares the whole
			// conversation, so clear any previous override.
			setOverrideMessageId(messageId);
			setIsOpen(true);
			// Pass the just-selected boundary explicitly: the `overrideMessageId`
			// state update above has not committed yet, so the callbacks would
			// otherwise capture the previous boundary from closure.
			const requestedBoundary =
				messageId && validBoundaryMessageIds.has(messageId) ? messageId : latestMessageId;
			setSeeWhatOthersCanSeeHref(buildPreviewHref(requestedBoundary));
			void hydrateShareDetails(requestedBoundary);
		},
		[
			buildPreviewHref,
			clearShareError,
			hydrateShareDetails,
			latestMessageId,
			validBoundaryMessageIds,
			conversationId,
			experienceTracker,
		],
	);

	const close = useCallback(() => {
		// Closed without copying the link and without an error — the user
		// abandoned the flow, so abort rather than fail to keep it out of
		// the SLO failure count.
		abortShareSession('Share conversation modal closed before the link was copied');
		shareDetailsRequestIdRef.current += 1;
		clearShareError();
		setShareUrl(undefined);
		setSeeWhatOthersCanSeeHref(undefined);
		setInitialShareType(undefined);
		setInitialSelectedUsers(undefined);
		setInitialOwnerUser(undefined);
		setIsSavingShareAccess(false);
		setIsLoadingInitialState(false);
		confirmedShareAccessRef.current = undefined;
		desiredShareAccessRef.current = undefined;
		pendingShareAccessRef.current = undefined;
		shareAccessSavePromiseRef.current = undefined;
		setOverrideMessageId(undefined);
		setIsOpen(false);
	}, [abortShareSession, clearShareError]);

	const replaceShareAccess = useCallback(
		async (payload: ShareConversationSubmitPayload): Promise<ShareDetailsResponse> => {
			if (!hasActiveConversation || !conversationId) {
				throw new Error('No active conversation to share.');
			}
			if (isRovoResponseInProgress || !boundaryMessageId) {
				throw new Error('Wait for Rovo to finish before sharing this chat.');
			}

			const isOrg = payload.shareType === 'organization';
			const allowedUserAris = withoutCurrentUserAri(payload.addUserAris, currentUserId);
			return assistanceService.replaceConversationShare({
				conversationId,
				// Use the resolved boundary (a valid per-message override,
				// otherwise the latest shareable message) so "share up to
				// here" is honoured on the share-details API path too.
				messageId: boundaryMessageId,
				shareType: isOrg ? 'ORG' : 'SPECIFIC_USERS',
				...(isOrg ? {} : { allowedUserAris }),
			});
		},
		[
			assistanceService,
			boundaryMessageId,
			conversationId,
			currentUserId,
			hasActiveConversation,
			isRovoResponseInProgress,
		],
	);

	const defaultOnSubmit = useCallback(
		async (payload: ShareConversationSubmitPayload): Promise<string> => {
			if (!hasActiveConversation || !conversationId) {
				// Should be unreachable because Copy link is
				// disabled in this state, but guard anyway so a misuse
				// surfaces as an inline error instead of a 400 from the
				// backend.
				throw new Error('No active conversation to share.');
			}
			if (isRovoResponseInProgress || !boundaryMessageId) {
				throw new Error('Wait for Rovo to finish before sharing this chat.');
			}

			try {
				const response = await replaceShareAccess(payload);
				return applyShareDetailsResponse(response, { updateInitialState: false });
			} catch (err) {
				await hydrateShareDetails();
				throw err;
			}
		},
		[
			applyShareDetailsResponse,
			replaceShareAccess,
			conversationId,
			hasActiveConversation,
			hydrateShareDetails,
			isRovoResponseInProgress,
			boundaryMessageId,
		],
	);

	const isMessageShareable = useCallback(
		(messageId: string): boolean =>
			// Sharing is only possible while there is an active conversation
			// that is not mid-response; beyond that, the message itself must be
			// a valid boundary under the shared shareability rule.
			hasActiveConversation &&
			!isRovoResponseInProgress &&
			isUuidMessageId(messageId) &&
			validBoundaryMessageIds.has(messageId),
		[hasActiveConversation, isRovoResponseInProgress, validBoundaryMessageIds],
	);

	const submitShareAccessChange = useCallback(
		async (payload: ShareConversationSubmitPayload) => {
			if (!onSubmit) {
				const response = await replaceShareAccess(payload);
				const confirmedPayload = toShareAccessPayload(response, currentUserId);
				const shouldUpdateInitialState = accessPayloadsEqual(
					confirmedPayload,
					desiredShareAccessRef.current,
				);
				const resultUrl = applyShareDetailsResponse(response, {
					resetDesiredAccess: shouldUpdateInitialState,
					updateInitialState: shouldUpdateInitialState,
				});
				setShareUrl(resultUrl);
				setSeeWhatOthersCanSeeHref(buildPreviewHref());
				return;
			}

			const resultUrl = await onSubmit(payload);
			if (resultUrl) {
				setShareUrl(resultUrl);
				setSeeWhatOthersCanSeeHref(buildPreviewHref());
			}
			const shouldUpdateInitialState = accessPayloadsEqual(payload, desiredShareAccessRef.current);
			applyConfirmedShareAccess(payload, {
				resetDesiredAccess: shouldUpdateInitialState,
				updateInitialState: shouldUpdateInitialState,
			});
		},
		[
			applyConfirmedShareAccess,
			applyShareDetailsResponse,
			currentUserId,
			buildPreviewHref,
			onSubmit,
			replaceShareAccess,
		],
	);

	const drainShareAccessQueue = useCallback((): Promise<void> => {
		if (shareAccessSavePromiseRef.current) {
			return shareAccessSavePromiseRef.current;
		}

		const savePromise = Promise.resolve().then(async () => {
			setIsSavingShareAccess(true);
			try {
				let nextPayload = pendingShareAccessRef.current;
				while (nextPayload && !accessPayloadsEqual(nextPayload, confirmedShareAccessRef.current)) {
					pendingShareAccessRef.current = undefined;
					try {
						await submitShareAccessChange(nextPayload);
					} catch (err) {
						const confirmedShareAccess = confirmedShareAccessRef.current;
						desiredShareAccessRef.current = confirmedShareAccess;
						pendingShareAccessRef.current = undefined;
						applyConfirmedShareAccess(confirmedShareAccess);
						reportShareError(err);
						throw err;
					}
					nextPayload = pendingShareAccessRef.current;
				}
				pendingShareAccessRef.current = undefined;
			} finally {
				shareAccessSavePromiseRef.current = undefined;
				setIsSavingShareAccess(false);
			}
		});

		shareAccessSavePromiseRef.current = savePromise;
		return savePromise;
	}, [applyConfirmedShareAccess, submitShareAccessChange, reportShareError]);

	const handleCopyLink = useCallback(
		async (payload: ShareConversationSubmitPayload): Promise<string | undefined> => {
			const submit = onSubmit ?? defaultOnSubmit;
			setIsSubmitting(true);
			clearShareError();
			try {
				const resultUrl = await submit(payload);
				if (resultUrl) {
					setShareUrl(resultUrl);
					setSeeWhatOthersCanSeeHref(buildPreviewHref());
					return resultUrl;
				}
				setIsOpen(false);
			} catch (err) {
				reportShareError(err);
				// Propagate so the modal's copy flow rejects with the real cause instead of
				// falling through to its generic "No share link was available to copy." error.
				throw err;
			} finally {
				setIsSubmitting(false);
			}
			return undefined;
		},
		[buildPreviewHref, clearShareError, defaultOnSubmit, onSubmit, reportShareError],
	);

	const handleShareAccessChange = useCallback(
		(payload: ShareConversationSubmitPayload) => {
			desiredShareAccessRef.current = payload;
			pendingShareAccessRef.current = payload;
			clearShareError();
			return drainShareAccessQueue()
				.then(() => true)
				.catch(() => false);
		},
		[clearShareError, drainShareAccessQueue],
	);

	const flushShareAccessChanges = useCallback(async () => {
		const desiredShareAccess = desiredShareAccessRef.current;
		if (
			desiredShareAccess &&
			!accessPayloadsEqual(desiredShareAccess, confirmedShareAccessRef.current)
		) {
			pendingShareAccessRef.current = desiredShareAccess;
		}

		await drainShareAccessQueue();
	}, [drainShareAccessQueue]);

	return {
		isOpen,
		isSubmitting,
		isLoadingInitialState,
		errorMessage,
		errorCode,
		canGenerate,
		shareDisabledReason,
		shareUrl,
		seeWhatOthersCanSeeHref,
		initialShareType,
		initialSelectedUsers,
		initialOwnerUser,
		autoSaveShareAccess: Boolean(shareUrl),
		isSavingShareAccess,
		allowEmptySpecificUsers: true,
		showSpecificPeopleAccessList: true,
		open,
		close,
		handleShareAccessChange,
		flushShareAccessChanges,
		handleCopyLinkSuccess: succeedShareSession,
		handleCopyLinkFailure: failShareSession,
		handleCopyLink,
		isMessageShareable,
	};
};
